import Box from "@mui/material/Box";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import Grow from "@mui/material/Grow";
import Paper from "@mui/material/Paper";
import Popper from "@mui/material/Popper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";

import { NamedDateRange } from "./utils";

type MoreRangeDropdownProps = {
  open: boolean;
  anchorEl: any;
  lastDaysAgo: string;
  handleClose: (event: Event) => void;
  currentSelectedRange: string;
  onLastDaysChanged: (value: string) => void;
  includeDateIntervalOptions?: boolean;
  onChange: (
    event: React.MouseEvent<HTMLElement> | undefined,
    newRange: NamedDateRange
  ) => void;
};
export default function MoreRangeDropdown({
  open,
  anchorEl,
  lastDaysAgo,
  handleClose,
  currentSelectedRange,
  onLastDaysChanged,
  onChange,
  includeDateIntervalOptions = false
}: MoreRangeDropdownProps) {
  const theme = useTheme();
  return (
    <Popper
      sx={{
        zIndex: 100,
        boxShadow: theme.shadows[3]
      }}
      open={open}
      anchorEl={anchorEl}
      role={undefined}
      transition
      disablePortal
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom" ? "center top" : "center bottom"
          }}
        >
          <Paper>
            <ClickAwayListener onClickAway={handleClose}>
              <Box
                sx={{
                  p: 1,
                  borderRadius: "4px"
                }}
              >
                <Stack
                  direction="row"
                  sx={{ pt: 1, px: 1, alignItems: "center", mb: 2 }}
                >
                  <Typography
                    sx={{ mr: 1, minWidth: "55px", color: grey[700] }}
                  ></Typography>

                  <Stack>
                    <TextField
                      name="lastDaysAgo"
                      size="small"
                      label="Last Days Ago"
                      variant="outlined"
                      type="number"
                      inputProps={{
                        min: 0,
                        max: 365,
                        maxLength: 3
                      }}
                      sx={{ width: "165px" }}
                      value={lastDaysAgo}
                      onChange={(event) => {
                        onLastDaysChanged(event.currentTarget.value);
                      }}
                    />
                  </Stack>
                </Stack>
                {includeDateIntervalOptions && (
                  <Stack
                    direction="row"
                    sx={{ px: 1, alignItems: "center", mb: 2 }}
                  >
                    <Typography sx={{ color: grey[700], mr: 1 }}>
                      to Date
                    </Typography>
                    <ToggleButtonGroup
                      color="primary"
                      value={currentSelectedRange}
                      exclusive
                      onChange={onChange}
                      sx={{
                        backgroundColor: theme.palette.common.white
                      }}
                    >
                      <ToggleButton size="small" value="today">
                        Today
                      </ToggleButton>
                      <ToggleButton size="small" value="week">
                        WTD
                      </ToggleButton>
                      <ToggleButton size="small" value="month">
                        MTD
                      </ToggleButton>
                      <ToggleButton size="small" value="quarter">
                        QTD
                      </ToggleButton>
                      <ToggleButton size="small" value="year">
                        YTD
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                )}

                <Stack direction="row" sx={{ px: 1, alignItems: "center" }}>
                  <Typography
                    sx={{ color: grey[700], mr: 1, minWidth: "55px" }}
                  >
                    Month
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={currentSelectedRange}
                    exclusive
                    onChange={onChange}
                    sx={{
                      backgroundColor: theme.palette.common.white
                    }}
                  >
                    <ToggleButton size="small" value="jan">
                      Jan
                    </ToggleButton>
                    <ToggleButton size="small" value="feb">
                      Feb
                    </ToggleButton>
                    <ToggleButton size="small" value="mar">
                      Mar
                    </ToggleButton>
                    <ToggleButton size="small" value="apr">
                      Apr
                    </ToggleButton>
                    <ToggleButton size="small" value="may">
                      May
                    </ToggleButton>
                    <ToggleButton size="small" value="jun">
                      Jun
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack
                  direction="row"
                  sx={{ px: 1, alignItems: "center", mb: 2 }}
                >
                  <Typography
                    sx={{ color: grey[700], mr: 1, minWidth: "55px" }}
                  ></Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={currentSelectedRange}
                    exclusive
                    onChange={onChange}
                    sx={{
                      backgroundColor: theme.palette.common.white
                    }}
                  >
                    <ToggleButton size="small" value="jul">
                      Jul
                    </ToggleButton>
                    <ToggleButton size="small" value="aug">
                      Aug
                    </ToggleButton>
                    <ToggleButton size="small" value="sep">
                      Sep
                    </ToggleButton>
                    <ToggleButton size="small" value="oct">
                      Oct
                    </ToggleButton>
                    <ToggleButton size="small" value="nov">
                      Nov
                    </ToggleButton>
                    <ToggleButton size="small" value="dec">
                      Dec
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Stack
                  direction="row"
                  sx={{ px: 1, alignItems: "center", mb: 1 }}
                >
                  <Typography sx={{ color: grey[700], mr: 1 }}>
                    Quarter
                  </Typography>
                  <ToggleButtonGroup
                    color="primary"
                    value={currentSelectedRange}
                    exclusive
                    onChange={onChange}
                    sx={{
                      backgroundColor: theme.palette.common.white
                    }}
                  >
                    <ToggleButton size="small" value="quarter1">
                      Q1
                    </ToggleButton>
                    <ToggleButton size="small" value="quarter2">
                      Q2
                    </ToggleButton>
                    <ToggleButton size="small" value="quarter3">
                      Q3
                    </ToggleButton>
                    <ToggleButton size="small" value="quarter4">
                      Q4
                    </ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
              </Box>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
}
